/**
 * 统一"停止"行为的全局协调器
 *
 * 核心职责：
 * 1. module-level AbortController 单例 —— 跨组件保活，任何 fetch 可注册到它上面
 * 2. zustand store 暴露"停止中"状态给 UI
 *
 * 老板需求（2026-06-02）："不管是哪个页面，只要用户点'停止'，然后弹窗点确定后，
 * 就要立即结算并停止所有模型的调用"
 *
 * 用法：
 *   // 启动新 generation 时：
 *   const signal = getGlobalAbortSignal();
 *   fetch('/api/...', { signal });
 *
 *   // 用户点停止时：
 *   globalAbortAll('user_stopped');
 *
 * 注意事项：
 * - abortAll() 后会自动重建 controller，所以下次启动 generation 有干净 signal
 * - 多个组件可同时调用 registerAbortController，重复 id 会被覆盖
 * - unregisterAbortController 必须在组件 unmount 时调用，避免内存泄漏
 *
 * @see docs/stop-coordinator-production-plan.md Phase 0
 */

import { create } from 'zustand';

// ── 模块级单例：跨页面/跨组件保活 ──

let globalAbortController: AbortController | null = null;

/** 获取当前全局 AbortController（懒创建） */
function getController(): AbortController {
  if (!globalAbortController) {
    globalAbortController = new AbortController();
  }
  return globalAbortController;
}

/** 重建 AbortController，返回新的 signal（用于下次启动 generation） */
function recreateController(): AbortController {
  globalAbortController = new AbortController();
  return globalAbortController;
}

// ── 公开 API ──

/**
 * 立即取消所有通过 getGlobalAbortSignal() 注册的 fetch
 *
 * @param reason 取消原因，写入 AbortSignal.reason，方便日志排查
 */
export function globalAbortAll(reason: string = 'user_stopped'): void {
  const c = getController();
  if (!c.signal.aborted) {
    c.abort(reason);
  }
  // 重建 controller，下次启动 generation 时有干净 signal
  // 不重建的话，下一次 getGlobalAbortSignal() 会返回已 aborted 的 signal
  recreateController();
}

/**
 * 获取当前未 aborted 的 signal（用于新启动的 fetch 注入）
 *
 * 注意：如果在 abort 之后调用，会返回新的（未 aborted）signal
 */
export function getGlobalAbortSignal(): AbortSignal {
  return getController().signal;
}

/**
 * 重置全局 controller（在 generation 重新开始时调用，可选）
 *
 * 通常不需要显式调用，因为 abortAll() 内部已经重建了 controller
 */
export function resetGlobalAbortController(): AbortController {
  return recreateController();
}

/**
 * 检查全局 signal 是否已经被 abort
 */
export function isGlobalAbortSignalAborted(): boolean {
  return getController().signal.aborted;
}

// ── zustand store：暴露"停止中"状态给 UI ──

interface GlobalStopState {
  /** 是否正在执行停止流程（用于显示 loading） */
  isStopping: boolean;
  /** 上次结算金额（用于弹窗显示"已结算 ¥X"） */
  lastCost: { minutes: number; cost: number } | null;
  setStopping: (v: boolean) => void;
  setLastCost: (c: { minutes: number; cost: number } | null) => void;
}

export const useGlobalStopStore = create<GlobalStopState>((set) => ({
  isStopping: false,
  lastCost: null,
  setStopping: (v) => set({ isStopping: v }),
  setLastCost: (c) => set({ lastCost: c }),
}));
